import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import Text from "@ui/Text";
import Modal from "@ui/ModalV2";
import ModalForm from "@ui/ModalV2/Form";
import DropzoneV2 from "@ui/DropzoneV2";
import useModal from "@ui/lib/hooks/useModal";
import { usePost, usePut } from "../../../api/useCall";
import t from "../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import { MTS_ENV } from "../../../lib/setupEnv";
import { useSettings } from "../../../api/useSettings";
import { ActionLink } from "@ui/Link";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, UploadLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import { Markdown } from "@ui/Markdown";
import Button from "@ui/Button";
import { type LexicalEditor } from "lexical";
import { convertFromMarkdown } from "@ui/Markdown/plugins/common/MarkdownConverterPlugin";

function Legals_({ path }: { path: string }) {
  const { modal: opened, openModal, closeModal } = useModal();
  const updateSetting = usePut("/settings");
  const { addToast } = useToasts();

  const upload = usePost("/file");
  const [file, setFile] = useState<File | null>(null);
  const handleDrop = useCallback(([file]: File[]) => {
    if (!file) return;
    setFile(file);
  }, []);
  const saveLegals = useCallback(() => {
    if (!file) return false;
    const body = new FormData();
    body.append("path", `legals`);
    body.append("file", file);
    // @ts-ignore
    return upload({ body })
      .then(res =>
        updateSetting({
          body: {
            LEGAL_FILE_PATH: res.body.path,
          },
        }),
      )
      .then(() => {
        addToast("success", t("customization.settings.update.success"));
        return true;
      })
      .catch(() => {
        addToast("error", t("errors.unexpected_error"));
        return false;
      });
  }, [addToast, file, updateSetting, upload]);

  const handleClose = useCallback(() => {
    closeModal();
    setFile(null);
  }, [closeModal]);

  const validation = useMemo(() => (file ? file.size > 0 : undefined), [file]);

  const settings = useSettings();
  const editor = useRef<LexicalEditor | null>(null);
  const [markdown, setMarkdown] = useState<string>(settings.LEGAL_TEXT);
  const [version, setVersion] = useState<number>(0);
  const cancel = useCallback(() => {
    setMarkdown(settings.LEGAL_TEXT);
    setVersion(s => s++);
    editor.current?.update(() => convertFromMarkdown(settings.LEGAL_TEXT));
  }, [settings.LEGAL_TEXT]);
  const saveLegalPrompt = useCallback(
    () =>
      updateSetting({
        body: {
          LEGAL_TEXT: markdown,
        },
      })
        .then(() => {
          void addToast("success", t("customization.settings.update.success"));
          setVersion(s => s++);
        })
        .catch(() => void addToast("error", t("errors.unexpected_error"))),
    [addToast, markdown, updateSetting],
  );

  const clearLegalPath = useCallback(
    () =>
      updateSetting({
        body: {
          LEGAL_FILE_PATH: "",
        },
      })
        .then(() => {
          addToast("success", t("customization.settings.update.success"));
          return true;
        })
        .catch(() => {
          addToast("error", t("errors.unexpected_error"));
          return false;
        }),
    [addToast, updateSetting],
  );

  return (
    <>
      <Text type="body1" className={legalsSubtitle}>
        {t("common.legal_notice")}{" "}
        {path.length > 0 && (
          <ActionLink href={MTS_ENV.COREV2_ASSETS_URL + "/" + path} target="_blank">
            <Text as="span" type="body1">
              {path.slice(path.lastIndexOf("/") + 1)}
            </Text>
          </ActionLink>
        )}
        {path.length === 0 && "N/A"}
        <span className={legalsSubtitleAction}>
          <TooltipV2 overlay={t("common.file.import")}>
            <ButtonIconV2 onClick={openModal}>
              <UploadLine />
            </ButtonIconV2>
          </TooltipV2>
          <TooltipV2 overlay={t("modal.delete")}>
            <ButtonIconV2 onClick={clearLegalPath} disabled={path.length === 0}>
              <DeleteBinLine />
            </ButtonIconV2>
          </TooltipV2>
        </span>
      </Text>
      <Text type="body1SemiBold" className={legalsSubtitle}>
        {t("customization.legals.modal_prompt")}
      </Text>
      <Markdown
        ref={editor}
        key={version}
        editable
        defaultValue={settings.LEGAL_TEXT}
        onConversion={setMarkdown}
        autoFocus={false}
      />
      <div className={legalPromptActions}>
        <Button variant="ghost" onClick={cancel} disabled={markdown === settings.LEGAL_TEXT}>
          {t("commons.message_card.cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={saveLegalPrompt}
          disabled={markdown === settings.LEGAL_TEXT}
        >
          {t("commons.button.publish")}
        </Button>
      </div>
      <Modal onRequestClose={handleClose} isOpen={opened} title={t("common.legal_notice.title")}>
        <ModalForm onRequestClose={handleClose} onSubmit={saveLegals}>
          <DropzoneV2
            onDrop={handleDrop}
            accept={[".pdf"]}
            nameFile={file?.name}
            validation={validation}
          />
        </ModalForm>
      </Modal>
    </>
  );
}
export const Legals = memo(Legals_);

const legalsSubtitle = css`
  color: ${colors.carbon900};
  margin-top: ${spacings.xs};
  display: flex;
  align-items: center;
  gap: ${spacings.xs};

  &:nth-child(3) {
    color: ${colors.carbon800};
  }
`;

const legalsSubtitleAction = css`
  display: flex;
  gap: ${spacings.xxs};
  margin-left: ${spacings.xs};
`;

const legalPromptActions = css`
  display: flex;
  justify-content: flex-end;
  gap: ${spacings.m};
`;
